import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Loader2, Plus, Trash2, CheckCircle2, Lock } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IN = "mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-base";
const LB = "text-[10px] uppercase tracking-[0.2em] text-[#757067]";
const heute = () => new Date().toISOString().slice(0, 10);
const num = (v) => (v ?? 0).toFixed(2).replace(".", ",");

export default function MitarbeiterZeiten() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [monat, setMonat] = useState("");
  const [fehler, setFehler] = useState("");
  const [neu, setNeu] = useState({ datum: heute(), von: "", bis: "", pause_min: 0, notiz: "" });
  const [busy, setBusy] = useState(false);
  const [signature, setSignature] = useState("");
  const [name, setName] = useState("");
  const [tagOffen, setTagOffen] = useState(null);
  const [tagSig, setTagSig] = useState("");

  const tagBestaetigen = async (id) => {
    if (!tagSig) { toast.error("Bitte im Feld unterschreiben"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/mitarbeiter/by-token/${token}/zeiten/${id}/bestaetigen`, {
        signature_data_url: tagSig, name,
      });
      setTagOffen(null);
      setTagSig("");
      await load(monat);
      toast.success("Tag bestätigt — Lukas hat den Stundenzettel bekommen");
    } catch (err) { toast.error(err.response?.data?.detail || "Bestätigen fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const load = useCallback(async (m) => {
    try {
      const { data: d } = await axios.get(`${API}/mitarbeiter/by-token/${token}`, { params: m ? { monat: m } : {} });
      setData(d);
      setMonat(d.monat);
      setName((n) => n || d.mitarbeiter?.name || "");
    } catch (err) {
      setFehler(err.response?.data?.detail || "Link ungültig");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const speichern = async () => {
    if (!neu.datum || !neu.von || !neu.bis) { toast.error("Bitte Datum, von und bis angeben"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/mitarbeiter/by-token/${token}/zeiten`, {
        ...neu, pause_min: Number(neu.pause_min) || 0,
      });
      setNeu((n) => ({ ...n, von: "", bis: "", pause_min: 0, notiz: "" }));
      await load(monat);
      toast.success("Zeit gespeichert");
    } catch (err) { toast.error(err.response?.data?.detail || "Konnte nicht gespeichert werden"); }
    finally { setBusy(false); }
  };

  const loeschen = async (id) => {
    try {
      await axios.delete(`${API}/mitarbeiter/by-token/${token}/zeiten/${id}`);
      await load(monat);
    } catch (err) { toast.error(err.response?.data?.detail || "Löschen nicht möglich"); }
  };

  const einreichen = async () => {
    if (!signature) { toast.error("Bitte unten im Feld unterschreiben"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/mitarbeiter/by-token/${token}/einreichen`, {
        monat, signature_data_url: signature, name,
      });
      setSignature("");
      await load(monat);
      toast.success("Stunden abgeschickt — Lukas prüft sie jetzt");
    } catch (err) { toast.error(err.response?.data?.detail || "Abschicken fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  if (fehler) {
    return (
      <div className="min-h-screen bg-[#F2EFE9] flex items-center justify-center px-6" data-testid="ma-fehler">
        <p className="text-sm text-[#757067]">{fehler}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#F2EFE9] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#BA6D52]" />
      </div>
    );
  }

  const offen = data.status === "offen";

  return (
    <div className="min-h-screen bg-[#F2EFE9] py-10 px-4" data-testid="mitarbeiter-zeiten">
      <div className="max-w-2xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA6D52]">Lukas Stutz · Zeiterfassung</p>
        <h1 className="font-serif text-3xl md:text-4xl font-light tracking-tight mt-3 text-[#2C2A26]">
          Hallo {data.mitarbeiter.name}
        </h1>
        <p className="text-sm text-[#757067] mt-2">
          Trag hier Deine Arbeitszeiten ein. Am Monatsende schickst Du sie mit Deiner Unterschrift ab.
        </p>

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <label className={LB}>Monat</label>
          <select value={monat} onChange={(e) => { setMonat(e.target.value); load(e.target.value); }}
            data-testid="ma-monat-select"
            className="bg-white border border-[#E3DECE] px-3 py-2 text-sm">
            {(data.monate || []).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          {data.status !== "offen" && (
            <span className="text-[11px] uppercase tracking-[0.15em] text-[#BA6D52] border border-[#BA6D52] px-2 py-1"
              data-testid="ma-status-badge">
              {data.status === "eingereicht" ? "abgeschickt" : "freigegeben"}
            </span>
          )}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="bg-white border border-[#E3DECE] p-4" data-testid="ma-summe-stunden">
            <p className={LB}>Stunden {data.monat_label}</p>
            <p className="font-serif text-2xl text-[#2C2A26] mt-1">{num(data.summe_stunden)}</p>
          </div>
          <div className="bg-white border border-[#E3DECE] p-4" data-testid="ma-verdienst">
            <p className={LB}>Brutto bei {num(data.mitarbeiter.stundenlohn_eur)} €</p>
            <p className="font-serif text-2xl text-[#2C2A26] mt-1">{num(data.verdienst_eur)} €</p>
          </div>
        </div>
        {data.ueber_grenze && (
          <p className="mt-3 text-sm text-[#B03A2E]" data-testid="ma-grenze-warnung">
            Achtung: Du liegst über der Minijob-Grenze von {num(data.grenze_eur)} € — bitte mit Lukas abstimmen.
          </p>
        )}

        {offen ? (
          <div className="mt-8 bg-white border border-[#E3DECE] p-5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-4">Arbeitszeit eintragen</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className={LB}>Datum</label>
                <input type="date" value={neu.datum} data-testid="ma-datum"
                  onChange={(e) => setNeu((n) => ({ ...n, datum: e.target.value }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Von</label>
                <input type="time" value={neu.von} data-testid="ma-von"
                  onChange={(e) => setNeu((n) => ({ ...n, von: e.target.value }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Bis</label>
                <input type="time" value={neu.bis} data-testid="ma-bis"
                  onChange={(e) => setNeu((n) => ({ ...n, bis: e.target.value }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Pause (Minuten)</label>
                <input type="number" min="0" step="5" value={neu.pause_min} data-testid="ma-pause"
                  onChange={(e) => setNeu((n) => ({ ...n, pause_min: e.target.value }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Notiz (optional)</label>
                <input value={neu.notiz} data-testid="ma-notiz"
                  onChange={(e) => setNeu((n) => ({ ...n, notiz: e.target.value }))} className={IN} />
              </div>
            </div>
            <button onClick={speichern} disabled={busy} data-testid="ma-zeit-speichern"
              className="mt-5 w-full bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Zeit speichern
            </button>
          </div>
        ) : (
          <div className="mt-8 bg-white border border-[#E3DECE] p-5 flex items-start gap-3" data-testid="ma-gesperrt">
            <Lock size={18} className="text-[#BA6D52] mt-0.5" />
            <p className="text-sm text-[#575249]">
              {data.status === "eingereicht"
                ? "Du hast diesen Monat abgeschickt. Lukas prüft die Stunden — Änderungen bitte direkt bei ihm melden."
                : "Dieser Monat ist freigegeben und abgeschlossen."}
            </p>
          </div>
        )}

        <div className="mt-8">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-3">
            Deine Zeiten · {data.monat_label}
          </p>
          {(data.eintraege || []).length === 0 ? (
            <p className="text-sm text-[#757067]" data-testid="ma-keine-zeiten">Noch keine Zeiten eingetragen.</p>
          ) : (
            <div className="space-y-2">
              {data.eintraege.map((z) => (
                <div key={z.id} data-testid={`ma-zeile-${z.id}`}
                  className="bg-white border border-[#E3DECE] px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm text-[#2C2A26]">
                        {z.datum.split("-").reverse().join(".")} · {z.von}–{z.bis}
                        {z.pause_min ? ` · ${z.pause_min} Min. Pause` : ""}
                      </p>
                      {z.notiz && <p className="text-xs text-[#757067] mt-0.5">{z.notiz}</p>}
                      {z.bestaetigt_am && (
                        <p className="text-[10px] uppercase tracking-[0.15em] text-[#BA6D52] mt-1"
                          data-testid={`ma-tag-bestaetigt-${z.id}`}>
                          Tag bestätigt
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-[#2C2A26] inline-flex items-center gap-1">
                        <Clock size={13} className="text-[#BA6D52]" /> {num(z.stunden)}
                      </span>
                      {!z.bestaetigt_am && offen && (
                        <button onClick={() => { setTagOffen(tagOffen === z.id ? null : z.id); setTagSig(""); }}
                          data-testid={`ma-tag-btn-${z.id}`}
                          className="text-[10px] uppercase tracking-[0.15em] border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-3 py-2">
                          Tag bestätigen
                        </button>
                      )}
                      {offen && !z.bestaetigt_am && (
                        <button onClick={() => loeschen(z.id)} data-testid={`ma-del-${z.id}`}
                          className="text-[#757067] hover:text-red-700 border border-[#E3DECE] p-2">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                  {tagOffen === z.id && (
                    <div className="mt-4 border-t border-[#F0EBE0] pt-4" data-testid={`ma-tag-box-${z.id}`}>
                      <p className="text-sm text-[#575249]">
                        Ich bestätige, dass ich am {z.datum.split("-").reverse().join(".")} von {z.von} bis {z.bis} Uhr
                        gearbeitet habe ({num(z.stunden)} Std.).
                      </p>
                      <div className="mt-3">
                        <SignaturePad onChange={setTagSig} testId={`ma-tag-signature-${z.id}`} />
                      </div>
                      <button onClick={() => tagBestaetigen(z.id)} disabled={busy}
                        data-testid={`ma-tag-senden-${z.id}`}
                        className="mt-4 w-full bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-3 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2">
                        {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                        Stundenzettel für diesen Tag abschicken
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {offen && (data.eintraege || []).length > 0 && (
          <div className="mt-8 bg-white border border-[#E3DECE] p-5" data-testid="ma-einreichen-box">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-3">
              Monat abschicken
            </p>
            <p className="text-sm text-[#575249]">
              Ich bestätige, dass die eingetragenen Zeiten für {data.monat_label} richtig sind
              ({num(data.summe_stunden)} Std.).
            </p>
            <div className="mt-4">
              <label className={LB}>Dein Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="ma-name" className={IN} />
            </div>
            <div className="mt-4">
              <label className={LB}>Unterschrift</label>
              <div className="mt-2">
                <SignaturePad onChange={setSignature} testId="ma-signature" />
              </div>
            </div>
            <button onClick={einreichen} disabled={busy} data-testid="ma-einreichen"
              className="mt-5 w-full bg-[#2C2A26] text-white hover:bg-[#1F1D1A] px-5 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Stunden abschicken
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
